import { linkPath } from 'common/constants';

/**
 * Tracks where the user was BEFORE entering the upload flow so the
 * Discard action can return them there with their original state instead
 * of defaulting to a blank /dashboard. Backed by sessionStorage so it
 * survives page steps inside the upload flow.
 *
 * We also stash the React Router history index at entry time so discard
 * can `navigate(-N)` and truly rewind history — this means the entry
 * page renders from its original history entry (with its location.state)
 * AND the browser back button no longer goes back into the upload flow.
 */
const KEY = 'uploadEntry';

export type UploadEntry = {
  pathname: string;
  search?: string;
  /** React Router stamps history.state with { idx, usr, key }. We keep
   *  idx so the discard handler can compute the rewind depth. */
  historyIdx?: number;
  /** The category or group the upload started from, for the done screen's
   *  "Back to ..." button. Unset when the flow started anywhere else. */
  label?: string;
};

const readHistoryIdx = (): number | undefined => {
  try {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    return typeof idx === 'number' ? idx : undefined;
  } catch {
    return undefined;
  }
};

export const setUploadEntryPath = (
  pathname: string,
  search = '',
  options: {
    // False when the caller has no history index worth recording - e.g. a
    // shortcut deep link, which runs its setup AFTER the app has already
    // navigated onto the upload screen itself. Stamping that own index
    // would make Discard's idx-rewind branch below fire and rewind onto
    // /document/upload instead of falling through to the pathname-navigate
    // branch. Defaults to true (record it), matching every pre-existing
    // caller (the "+" button, which calls this from the dashboard).
    recordHistoryIdx?: boolean;
    label?: string;
  } = {},
): void => {
  try {
    const payload: UploadEntry = {
      pathname,
      search,
      historyIdx: options?.recordHistoryIdx === false ? undefined : readHistoryIdx(),
      label: options?.label,
    };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
};

export const popUploadEntryPath = (): UploadEntry | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UploadEntry;
  } catch {
    return null;
  }
};

// Reads the entry without consuming it, for screens that only need to know
// where the flow began (the done screen's button label).
export const peekUploadEntryPath = (): UploadEntry | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as UploadEntry) : null;
  } catch {
    return null;
  }
};

// Category, group, vehicle and property pages: the places an upload can start
// from with a preset. Coming back to them after saving lets people see the
// new Doq sitting where they put it (Catherine: "I always want to check that
// it saved there correctly").
const CATEGORY_ENTRY_PREFIXES = ['/documents/viewCategory/', '/documents/viewGroup/', '/vehicleList/', '/propertyList/'];

export const isCategoryEntry = (entry: UploadEntry | null): boolean => (
  !!entry && CATEGORY_ENTRY_PREFIXES.some((prefix) => entry.pathname.startsWith(prefix))
);

export const getCurrentHistoryIdx = readHistoryIdx;

/**
 * Cleanly exit the upload flow. Rewinds history back to the entry idx so
 * the in-between upload steps disappear from the back stack, then issues
 * a push to `targetPath` (which clears the forward stack). If history idx
 * is unavailable, falls back to a plain push.
 *
 * Pass a `navigate` from `useNavigate()`. We accept `unknown` and narrow
 * internally because react-router's `NavigateFunction` is an overloaded
 * signature that doesn't compose into a single function type.
 */
type NavigateLike = {
  // Overload 1: numeric delta (history.go).
  (delta: number): void;
  // Overload 2: path + options.
  (to: string, options?: { replace?: boolean; state?: unknown }): void;
};

export const exitUploadFlow = (
  navigate: NavigateLike,
  targetPath: string,
  targetState?: unknown,
): void => {
  const entry = popUploadEntryPath();
  const currentIdx = readHistoryIdx();
  // Rewind first so the forward stack still contains the upload steps,
  // then the push clears the forward stack — leaving history clean.
  if (entry?.historyIdx != null && currentIdx != null && currentIdx > entry.historyIdx) {
    navigate(entry.historyIdx - currentIdx);
    // history.go is async — defer the push to the next tick so the
    // rewind has settled. React Router queues this fine.
    setTimeout(() => navigate(targetPath, { state: targetState }), 0);
    return;
  }
  navigate(targetPath, { replace: true, state: targetState });
};

/**
 * Leave the upload flow by going back to the page it started from, the same
 * way Discard does: rewind history so the page renders from its original
 * entry (ViewCategory needs its location.state, the URL alone is not enough)
 * and the back button no longer leads into the flow. Without a usable history
 * index it falls back to the dashboard rather than a stateless category URL.
 */
export const returnToUploadEntry = (navigate: NavigateLike): void => {
  const entry = popUploadEntryPath();
  const currentIdx = readHistoryIdx();
  if (entry?.historyIdx != null && currentIdx != null && currentIdx > entry.historyIdx) {
    navigate(entry.historyIdx - currentIdx);
    return;
  }
  navigate(linkPath.dashboard, { replace: true });
};
